import { keepPreviousData, useQuery } from "@tanstack/react-query";

import type { ApiClient } from "../client";
import { useApiClient } from "../useApiClient";

import { ANALYTICS_QUERY_KEY, toAnalyticsQuery } from "./useSummary";
import type { AnalyticsFilters } from "./useSummary";

// Shared query definition reused by the hook and by nav prefetch.
export function calendarQueryOptions(client: ApiClient, filters: AnalyticsFilters = {}) {
  return {
    queryKey: [...ANALYTICS_QUERY_KEY, "calendar", filters],
    queryFn: async () => {
      const { data, error } = await client.GET("/api/analytics/calendar", {
        params: { query: toAnalyticsQuery(filters) },
      });
      if (error !== undefined) {
        throw new Error("Failed to load calendar");
      }
      return data;
    },
  };
}

export function useCalendar(
  filters: AnalyticsFilters = {},
  options: { enabled?: boolean } = {},
) {
  const client = useApiClient();
  return useQuery({
    ...calendarQueryOptions(client, filters),
    // A filter window that doesn't overlap the visible month/year yields an
    // inverted [since, until] range the API rejects. Callers disable the query
    // in that case so the calendar renders empty instead of erroring.
    enabled: options.enabled ?? true,
    // Keep the previous month/year on screen while the next one loads so
    // navigating with the arrows doesn't flash a "Loading calendar..." state.
    placeholderData: keepPreviousData,
  });
}
